import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, TextField, Button, Stack, Chip, CircularProgress, Alert } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import modelsService from '../services/modelsService';

const EditModelPage = () => {
  const { id } = useParams();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { addSystemNotification } = useNotifications();

  useEffect(() => {
    const loadModel = async () => {
      try {
        const model = await modelsService.getModel(id);
        if (model) {
          // Vérifier que l'utilisateur a le droit de modifier ce modèle
          if (model.createdBy !== currentUser?.uid) {
            setError('Vous n\'avez pas les droits pour modifier ce modèle');
            setLoading(false);
            return;
          }

          setTitle(model.title || '');
          setDescription(model.description || '');
          setTags(model.tags || []);
          setPrompt(model.prompt || '');
        } else {
          setError('Modèle non trouvé');
        }
      } catch (error) {
        addSystemNotification('Erreur lors du chargement du modèle:', 'error');
        setError('Erreur lors du chargement du modèle');
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      loadModel();
    }
  }, [id, currentUser]);

  const handleAddTag = () => {
    if (tagInput && !tags.includes(tagInput)) {
      setTags([...tags, tagInput]);
      setTagInput('');
    }
  };

  const handleDeleteTag = (tagToDelete) => {
    setTags(tags.filter((tag) => tag !== tagToDelete));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !prompt.trim()) {
      setError('Le titre et le prompt sont requis');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await modelsService.updateModel(id, {
        title: title.trim(),
        description: description.trim(),
        tags,
        prompt: prompt.trim()
      });
      
      navigate('/models?filter=mine');
    } catch (error) {
      addSystemNotification('Erreur lors de la modification du modèle:', 'error');
      setError('Erreur lors de la modification du modèle');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/models')}>
          Retour aux modèles
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', py: 4 }}>
      <Typography variant="h4" fontWeight={700} mb={3}>Modifier le modèle</Typography>
      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField label="Titre" value={title} onChange={e => setTitle(e.target.value)} required fullWidth />
          <TextField label="Description" value={description} onChange={e => setDescription(e.target.value)} multiline minRows={2} fullWidth />
          <Box>
            <TextField label="Ajouter un tag" value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={handleKeyPress} sx={{ mr: 1 }} />
            <Button onClick={handleAddTag} variant="outlined">Ajouter</Button>
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
              {tags.map((tag, idx) => (
                <Chip key={idx} label={tag} onDelete={() => handleDeleteTag(tag)} color="primary" />
              ))}
            </Stack>
          </Box>
          <TextField label="Prompt" value={prompt} onChange={e => setPrompt(e.target.value)} multiline minRows={2} fullWidth />
          <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>Enregistrer</Button>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </form>
    </Box>
  );
};

export default EditModelPage; 